import { useState } from "react";

export function InlineVideoBlock({
  url,
  alt,
  compact,
}: {
  url: string;
  alt?: string;
  compact?: boolean;
}) {
  const [errored, setErrored] = useState(false);
  const sizeCls = compact
    ? "max-w-[min(240px,70vw)] max-h-[min(240px,70vw)] sm:max-w-[240px] sm:max-h-[240px]"
    : "max-w-[min(320px,80vw)] max-h-[min(320px,80vw)] sm:max-w-[320px] sm:max-h-[320px]";
  const caption = alt?.trim();

  if (errored) {
    return (
      <div
        className={`my-1.5 rounded-lg border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 ${compact ? "ds-micro px-2 py-1.5" : "ds-caption px-3 py-2"} opacity-70`}
      >
        <span className="font-semibold">Video</span>
        {caption ? ` — ${caption}` : ""}
        <span className="ml-1 opacity-60">(preview unavailable)</span>
      </div>
    );
  }

  return (
    <div className="my-1.5 flex flex-col gap-1">
      {caption && (
        <span className="ds-micro-strong uppercase opacity-70">
          {caption}
        </span>
      )}
      <video
        src={url}
        controls
        preload="metadata"
        onError={() => setErrored(true)}
        className={`rounded-lg border border-black/10 dark:border-white/10 object-cover ${sizeCls}`}
      >
        <track kind="captions" />
      </video>
    </div>
  );
}

export function InlineImageBlock({
  url,
  alt,
  compact,
}: {
  url: string;
  alt?: string;
  compact?: boolean;
}) {
  const [errored, setErrored] = useState(false);
  const sizeCls = compact
    ? "max-w-[min(200px,70vw)] max-h-[min(200px,70vw)] sm:max-w-[200px] sm:max-h-[200px]"
    : "max-w-[min(280px,80vw)] max-h-[min(280px,80vw)] sm:max-w-[280px] sm:max-h-[280px]";
  const caption = alt?.trim();

  if (errored) {
    return (
      <div
        className={`my-1.5 rounded-lg border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 ${compact ? "ds-micro px-2 py-1.5" : "ds-caption px-3 py-2"} opacity-70`}
      >
        <span className="font-semibold">Image</span>
        {caption ? ` — ${caption}` : ""}
        <span className="ml-1 opacity-60">(preview unavailable)</span>
      </div>
    );
  }

  return (
    <div className="my-1.5 flex flex-col gap-1">
      {caption && (
        <span className="ds-micro-strong uppercase opacity-70">
          {caption}
        </span>
      )}
      <a href={url} target="_blank" rel="noopener noreferrer" className="inline-block">
        <img
          src={url}
          alt={caption || "Image"}
          loading="lazy"
          onError={() => setErrored(true)}
          className={`rounded-lg border border-black/10 dark:border-white/10 object-cover ${sizeCls}`}
        />
      </a>
    </div>
  );
}

export function InlineMetaPreviewBlock({
  url,
  caption,
  compact,
}: {
  url: string;
  caption?: string;
  compact?: boolean;
}) {
  return (
    <div className="my-2 flex flex-col gap-1 max-w-full">
      {caption && (
        <span className="text-[10px] font-semibold uppercase tracking-wide opacity-70 break-words">
          {caption}
        </span>
      )}
      <div className="rounded-lg border border-black/10 dark:border-white/10 overflow-hidden bg-black/5 dark:bg-white/5">
        <iframe
          src={url}
          title={caption || "Meta ad preview"}
          className={`block w-full ${compact ? "h-[420px]" : "h-[480px]"} border-0`}
          scrolling="yes"
          allow="autoplay"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="ds-micro opacity-60 hover:opacity-100 underline self-start"
      >
        Open preview ↗
      </a>
    </div>
  );
}

export function EditPromptHint({ compact }: { compact?: boolean }) {
  return (
    <div
      className={`${compact ? "ds-micro mt-1 px-1" : "ds-micro mt-1.5 px-1"} italic text-muted-foreground/70`}
    >
      Want an edit — different copy, palette, or angle? Just type it here and I'll redo it.
    </div>
  );
}
